/**
 * Which merchant this location's card form opens against (ORDR-57).
 *
 * ordering.pirouz.ai serves many firms, one payment merchant each. Finix's
 * fraud session (`Finix.Auth(environment, merchantId)`) is scoped to a merchant,
 * and checkout charges through the site firm's own merchant, so the id must be
 * that firm's. paio-commerce resolves it server-side from the site and serves it
 * on the public menu for this site and location:
 *
 *   GET /api/v1/commerce/sites/<site>/locations/<location>/menu/
 *   data.payment = { provider: "finix", merchant_id: "MU...", card_payments_available: true }
 *
 * The browser sends no firm and no merchant. Anything other than an available
 * Finix merchant with a non-blank id, including a menu with no `payment` block
 * (a commerce that predates ORDR-57), a non-200 or a network failure, reads as
 * "not connected", so card fields are never mounted on a guess.
 *
 * MEMORY ONLY. The merchant id is public, but it is held in React state for the
 * session and never logged, stored or put in a URL.
 *
 * ONE REQUEST, TWO ANSWERS (ORDR-60). The same body carries the menu the diner
 * orders from (`publicMenu.ts`), so `readSiteMenu` reads both from one fetch.
 * A second caller of this URL would be a second, possibly different, menu.
 */

import type { ApiClient } from "../api/client.js";
import { catalogueFromPublicMenu, type CatalogueSection } from "../order/catalogue.js";

export type MenuPayment = { readonly kind: "card"; readonly merchantId: string } | { readonly kind: "not-connected" };

const NOT_CONNECTED: MenuPayment = { kind: "not-connected" };

export function menuPaymentPath(siteId: string, locationNodeId: string): string {
  return `/commerce/sites/${encodeURIComponent(siteId)}/locations/${encodeURIComponent(locationNodeId)}/menu/`;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

/** The menu body's `payment` block, read strictly. */
export function parseMenuPayment(body: unknown): MenuPayment {
  const payment = record(record(record(body)?.["data"])?.["payment"]);
  if (payment === null || payment["provider"] !== "finix" || payment["card_payments_available"] !== true) {
    return NOT_CONNECTED;
  }
  const merchantId = payment["merchant_id"];
  if (typeof merchantId !== "string" || merchantId.trim() === "") {
    return NOT_CONNECTED;
  }
  return { kind: "card", merchantId: merchantId.trim() };
}

/** One read of the public menu: what the diner sees and how they can pay. */
export interface SiteMenu {
  /** The same body as the app's own Menu and Item read it (ORDR-165). */
  readonly catalogue: CatalogueSection[];
  readonly payment: MenuPayment;
  /**
   * Present only when commerce REFUSED the read (403, ORDR-219). Measured in
   * production: a session signed in to another business is refused with
   * AUTHZ-002, and that was drawn as an empty menu. Never an empty menu.
   */
  readonly refused?: { readonly correlationId: string };
}

const NOTHING_SERVED: SiteMenu = { catalogue: [], payment: NOT_CONNECTED };

/**
 * Ask commerce for this location's menu and card merchant, in ONE request
 * (ORDR-60). A 403, whatever its code, is a REFUSAL and says so (ORDR-219).
 * A 404 (no such location, or nothing published), a 409 (two published
 * versions), any other non-200 and a network failure all read as an empty menu
 * with payment not connected. Never throws.
 */
export async function readSiteMenu(
  client: ApiClient,
  siteId: string,
  locationNodeId: string,
  language: string,
): Promise<SiteMenu> {
  try {
    const response = await client.request(menuPaymentPath(siteId, locationNodeId));
    if (response.status === 403) {
      return { ...NOTHING_SERVED, refused: { correlationId: response.correlationId } };
    }
    if (response.status !== 200) {
      return NOTHING_SERVED;
    }
    return {
      catalogue: catalogueFromPublicMenu(response.body, language),
      payment: parseMenuPayment(response.body),
    };
  } catch {
    return NOTHING_SERVED;
  }
}
